import { useCallback, useState } from 'react';
import { getBackendOrigin } from '@/services/apiBridge';

export function useTTS() {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // 用于播放服务器返回的音频流
  let currentAudioElement: HTMLAudioElement | null = null;

  const speak = useCallback(async (text: string, lang: string = 'zh-CN') => {
    if (!text || typeof text !== 'string') return;
    
    // Cancel previous speech/audio
    window.speechSynthesis.cancel();
    if (currentAudioElement) {
      currentAudioElement.pause();
      currentAudioElement = null;
    }
    setIsPaused(false);
    setIsSpeaking(true);

    try {
      // 优先使用服务器 TTS（如果配置了 voiceId）
      const voiceId = localStorage.getItem('xf_selected_voice_id');
      if (voiceId && voiceId !== 'undefined' && voiceId !== 'null' && voiceId.trim()) {
        const BASE = `${getBackendOrigin()}/api/voice`;
        const token = localStorage.getItem('xf_jwt_token');
        
        const res = await fetch(`${BASE}/synthesize`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({ text, voiceId }),
        });

        if (!res.ok) throw new Error(`TTS failed: ${res.status}`);
        
        const arrayBuffer = await res.arrayBuffer();
        const audioBlob = new Blob([arrayBuffer], { type: 'audio/mp3' });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        const audio = new Audio(audioUrl);
        audio.onended = () => { 
          setIsSpeaking(false); 
          setIsPaused(false);
          URL.revokeObjectURL(audioUrl); 
          currentAudioElement = null;
        };
        audio.onerror = () => { 
          setIsSpeaking(false); 
          setIsPaused(false);
          URL.revokeObjectURL(audioUrl);
          currentAudioElement = null;
        };
        currentAudioElement = audio;
        await audio.play();
      } else {
        // Fallback: browser speechSynthesis
        if (!window.speechSynthesis) {
          setIsSpeaking(false);
          return;
        }
        
        window.speechSynthesis.cancel();
        
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = lang;
        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        
        utterance.onstart = () => {
          setIsSpeaking(true);
          setIsPaused(false);
        };
        utterance.onend = () => {
          setIsSpeaking(false);
          setIsPaused(false);
        };
        utterance.onerror = () => {
          setIsSpeaking(false);
          setIsPaused(false);
        };
        
        window.speechSynthesis.speak(utterance);
      }
    } catch (err) {
      console.warn('[useTTS] Server TTS fallback:', err);
      setIsSpeaking(false);
      setIsPaused(false);
    }
  }, []);

  const stop = useCallback(() => {
    window.speechSynthesis.cancel();
    if (currentAudioElement) {
      currentAudioElement.pause();
      currentAudioElement = null;
    }
    setIsSpeaking(false);
    setIsPaused(false);
  }, []);

  const pause = useCallback(() => {
    if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
    if (currentAudioElement && !currentAudioElement.paused) {
      currentAudioElement.pause();
      setIsPaused(true);
    }
  }, []);

  const resume = useCallback(() => {
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    }
    if (currentAudioElement && currentAudioElement.paused) {
      currentAudioElement.play();
      setIsPaused(false);
    }
  }, []);

  return { speak, stop, pause, resume, isSpeaking, isPaused };
}
